import React, { useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  FaArrowLeft,
  FaMoneyBillWave,
  FaMobileAlt,
  FaLock,
  FaCheck,
  FaShoppingBag,
  FaUpload,
  FaTimes,
} from "react-icons/fa";

import API_BASE_URL from "../config/api";

import "./PaymentMethod.css";

function PaymentMethod() {
  const location = useLocation();
  const navigate = useNavigate();

const {
  product,
  quantity = 1,
  cartItems,
  customer,
  subtotal: passedSubtotal,
  deliveryCharges: passedDeliveryCharges,
  total: passedTotal,
} = location.state || {};

const orderItems =
  cartItems && cartItems.length > 0
    ? cartItems
    : product
    ? [
        {
          ...product,
          quantity,
        },
      ]
    : [];

  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [paymentProof, setPaymentProof] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [loading, setLoading] = useState(false);

  if (orderItems.length === 0 || !customer) {
    return (
      <div className="tv-payment-error">
        <h2>Order information not found</h2>

        <button onClick={() => navigate("/shop")}>
          BACK TO SHOP
        </button>
      </div>
    );
  }

const deliveryCharges = passedDeliveryCharges || 299;

const subtotal =
  passedSubtotal ??
  orderItems.reduce((sum, item) => {
    return (
      sum +
      Number(item.price || 0) * Number(item.quantity || 1)
    );
  }, 0);

const total =
  passedTotal ??
  subtotal + deliveryCharges;
const price =
  orderItems.length === 1
    ? Number(orderItems[0].price) || 0
    : subtotal;
  // ====
  // PAYMENT PROOF
  // ====

const handleProofChange = (e) => {
  const file = e.target.files[0];

  if (!file) return;

  // Only allow images
  if (!file.type.startsWith("image/")) {
    setPaymentError("Please select a valid image file.");
    return;
  }

  // 5MB limit
  if (file.size > 5 * 1024 * 1024) {
    setPaymentError("Payment proof image must be less than 5MB.");
    return;
  }

  setPaymentError("");
  setPaymentProof(file);
  setProofPreview(URL.createObjectURL(file));
};

  const removeProof = () => {
    setPaymentProof(null);
    setProofPreview("");
  };
  
  // ====
// OPEN WHATSAPP WITH ORDER DETAILS
// ====

const openWhatsApp = (orderId) => {
  const message = `
*TIME VALLEY - NEW ORDER*

Order ID: #${orderId}

*CUSTOMER DETAILS*
Name: ${customer.name}
Email: ${customer.email}
Phone: ${customer.phone}

*DELIVERY DETAILS*
Address: ${customer.address}
City: ${customer.city}
Postal Code: ${customer.postalCode || "N/A"}
Country: ${customer.country}
Notes: ${customer.notes || "N/A"}

*ORDER DETAILS*

${orderItems
  .map(
    (item, index) => `
${index + 1}. ${item.name}
Quantity: ${item.quantity || 1}
Price: Price :  ${(
      Number(item.price || 0) * Number(item.quantity || 1)
    ).toFixed(2)}
`
  )
  .join("\n")}

Delivery Charges: Price :  ${deliveryCharges.toFixed(2)}
Total: Price :  ${total.toFixed(2)}

*PAYMENT DETAILS*
Payment Method: ${
    paymentMethod === "JAZZCASH"
      ? "JazzCash"
      : "Cash on Delivery"
  }
Payment Status: ${
    paymentMethod === "COD"
      ? "Pending"
      : "Verification Pending"
  }

${
  paymentMethod === "JAZZCASH"
    ? `JazzCash Account Name: Hamza Aslam
JazzCash Number: 03244356346`
    : ""
}

Thank you for choosing *TIME VALLEY*.
`;

  const whatsappUrl = `https://wa.me/923249320453?text=${encodeURIComponent(
    message
  )}`;

  window.open(whatsappUrl, "_blank");
};
  // ====
  // PLACE ORDER
  // ====

const handlePlaceOrder = async () => {
  if (paymentMethod === "JAZZCASH" && !paymentProof) {
    setPaymentError("Please upload your JazzCash payment proof.");
    return;
  }

  setPaymentError("");
  setLoading(true);

  try {
    const formData = new FormData();

    // =========================
    // CUSTOMER DETAILS
    // =========================
    formData.append("customer_name", customer.name);
    formData.append("customer_email", customer.email);
    formData.append("customer_phone", customer.phone);
    formData.append("address", customer.address);
    formData.append("city", customer.city);
    formData.append("postal_code", customer.postalCode || "");
    formData.append("country", customer.country);
    formData.append("notes", customer.notes || "");

 // =========================
// PRODUCT / CART DETAILS
// =========================

formData.append(
  "product_id",
  orderItems[0]?.id || ""
);

formData.append(
  "product_name",
  orderItems[0]?.name || ""
);

formData.append(
  "product_image",
  orderItems[0]?.image1 || ""
);

formData.append(
  "quantity",
  orderItems.length === 1
    ? Number(orderItems[0]?.quantity || 1)
    : 1
);

formData.append("price", price);

formData.append("total", total);

formData.append(
  "delivery_charges",
  deliveryCharges
);

// Complete order items
formData.append(
  "order_items",
  JSON.stringify(
    orderItems.map((item) => ({
      id: item.id,
      name: item.name,
      image1: item.image1 || "",
      price: Number(item.price || 0),
      quantity: Number(item.quantity || 1),
    }))
  )
);

    // =========================
    // PAYMENT DETAILS
    // =========================
    formData.append("payment_method", paymentMethod);

    formData.append(
      "payment_status",
      paymentMethod === "COD"
        ? "Pending"
        : "Verification Pending"
    );

    // =========================
    // JAZZCASH PROOF
    // =========================
    if (paymentMethod === "JAZZCASH" && paymentProof) {
      formData.append("payment_proof", paymentProof);
    }

    // =========================
    // SEND ORDER TO BACKEND
    // =========================
    const response = await fetch(
      `${API_BASE_URL}/api/orders`,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(
        data.message || "Failed to place order"
      );
    }

    // ====
// OPEN WHATSAPP MANUALLY
// ====

openWhatsApp(data.orderId);

// ====
// GO TO ORDER SUCCESS
// ====

navigate("/order-success", {
  state: {
    orderId: data.orderId,
    paymentMethod,
    total,
    product:
      orderItems.length === 1
        ? orderItems[0]
        : null,
    quantity:
      orderItems.length === 1
        ? Number(orderItems[0]?.quantity || 1)
        : 1,
    cartItems: orderItems,
    customer,
    paymentProof: data.payment_proof || null,
  },
});

  } catch (error) {
    console.error("Order error:", error);

    setPaymentError(
      error.message ||
        "Something went wrong. Please try again."
    );

  } finally {
    setLoading(false);
  }
};

  return (
    <main className="tv-payment-page">
        {paymentError && (
      <div className="tv-payment-alert">
        <div className="tv-payment-alert-content">

          <div className="tv-payment-alert-icon">
            !
          </div>

          <div className="tv-payment-alert-text">
            <strong>Payment Notice</strong>
            <p>{paymentError}</p>
          </div>

          <button
            type="button"
            onClick={() => setPaymentError("")}
            className="tv-payment-alert-close"
          >
            <FaTimes />
          </button>

        </div>
      </div>
    )}
      <div className="tv-payment-container">

        {/* BACK */}

        <button
          className="tv-payment-back"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
          BACK TO CUSTOMER DETAILS
        </button>

        {/* HEADER */}

        <div className="tv-payment-header">
          <span>TIME VALLEY</span>

          <h1>
            Choose Your
            <em> Payment.</em>
          </h1>

          <p>
            Select your preferred payment method to complete
            your order.
          </p>
        </div>

        <div className="tv-payment-layout">

          {/* PAYMENT METHODS */}

          <section className="tv-payment-methods">

            <div className="tv-payment-section-title">
              <span>01</span>

              <div>
                <h2>Payment Method</h2>

                <p>Select one option below</p>
              </div>
            </div>

            {/* COD */}

            <button
              type="button"
              className={`tv-payment-option ${
                paymentMethod === "COD"
                  ? "active"
                  : ""
              }`}
              onClick={() => {
                setPaymentMethod("COD");
                removeProof();
              }}
            >
              <div className="tv-payment-icon">
                <FaMoneyBillWave />
              </div>

              <div className="tv-payment-info">
                <h3>Cash on Delivery</h3>

                <p>
                  Pay in cash when your order
                  arrives at your doorstep.
                </p>
              </div>

              <div className="tv-payment-radio">
                {paymentMethod === "COD" && (
                  <FaCheck />
                )}
              </div>
            </button>

            {/* JAZZCASH */}

            <button
              type="button"
              className={`tv-payment-option ${
                paymentMethod === "JAZZCASH"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setPaymentMethod("JAZZCASH")
              }
            >
              <div className="tv-payment-icon">
                <FaMobileAlt />
              </div>

              <div className="tv-payment-info">
                <h3>JazzCash</h3>

                <p>
                  Pay securely using your
                  JazzCash account.
                </p>
              </div>

              <div className="tv-payment-radio">
                {paymentMethod === "JAZZCASH" && (
                  <FaCheck />
                )}
              </div>
            </button>

            {/* JAZZCASH PAYMENT DETAILS */}

            {paymentMethod === "JAZZCASH" && (
              <div className="tv-jazzcash-box">

                <div className="tv-jazzcash-header">
                  <FaMobileAlt />

                  <div>
                    <strong>JazzCash Payment</strong>

                    <p>
                      Send your payment to the
                      following JazzCash account.
                    </p>
                  </div>
                </div>

                <div className="tv-jazzcash-details">

                  <div className="tv-jazzcash-detail">
                    <span>ACCOUNT NAME :  </span>

                    <strong>
                           Hamza Aslam
                    </strong>
                  </div>

                  <div className="tv-jazzcash-detail">
                    <span>JAZZCASH NUMBER : </span>

                    <strong>
                      03244356346
                    </strong>
                  </div>

                  <div className="tv-jazzcash-detail">
                    <span>AMOUNT TO PAY : </span>

                    <strong>
                      Price :  {total.toFixed(2)}
                    </strong>
                  </div>

                </div>

                {/* PAYMENT PROOF */}

                <div className="tv-payment-proof">

                  <label>
                    PAYMENT PROOF *  
                  </label>

                  {!proofPreview ? (
                    <label className="tv-proof-upload">

                      <FaUpload />

                      <span>
                        Upload Payment Screenshot  
                      </span>

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProofChange}
                      />

                    </label>
                  ) : (
                    <div className="tv-proof-preview">

                      <img
                        src={proofPreview}
                        alt="Payment proof"
                      />

                      <button
                        type="button"
                        onClick={removeProof}
                        className="tv-remove-proof"
                      >
                        <FaTimes />
                      </button>

                    </div>
                  )}

                </div>

              </div>
            )}

            {/* SUBMIT */}

            <button
              type="button"
              className="tv-payment-submit"
              onClick={handlePlaceOrder}
              disabled={loading}
            >
              <FaShoppingBag />

              {loading
                ? "PROCESSING..."
                : paymentMethod === "COD"
                ? "PLACE ORDER"
                : "PLACE JAZZCASH ORDER"}
            </button>

            <div className="tv-payment-secure">
              <FaLock />

              Your order information is securely handled.
            </div>

          </section>

          {/* ORDER SUMMARY */}

          <aside className="tv-payment-summary">

            <div className="tv-payment-summary-heading">
              <span>YOUR ORDER</span>

              <h2>Order Summary</h2>
            </div>

      {orderItems.map((item) => (
  <div
    className="tv-payment-product"
    key={item.id}
  >

    <div className="tv-payment-product-image">

      <img
        src={
          item.image1?.startsWith("http")
            ? item.image1
            : `${API_BASE_URL}/${item.image1?.replace(
                /^\/+/,
                ""
              )}`
        }
        alt={item.name}
      />

    </div>

    <div>

      <span>TIME VALLEY</span>

      <h3>{item.name}</h3>

      <p>
        Quantity: {item.quantity || 1}
      </p>

      <strong>
        Price : {" "}
        {(
          Number(item.price || 0) *
          Number(item.quantity || 1)
        ).toFixed(2)}
      </strong>

    </div>

  </div>
))}

            <div className="tv-payment-line" />

            <div className="tv-payment-row">
              <span>Price </span>

              <strong>
                 {subtotal.toFixed(2)}
              </strong>
            </div>

            <div className="tv-payment-row">
  <span>Total Items</span>

  <strong>
    {orderItems.reduce(
      (count, item) =>
        count + Number(item.quantity || 1),
      0
    )}
  </strong>
</div>

            <div className="tv-payment-row">
              <span>Delivery</span>

              <strong>
                Price :  {deliveryCharges.toFixed(2)}
              </strong>
            </div>

            <div className="tv-payment-total">
              <span>Total</span>

              <strong>
                Price :  {total.toFixed(2)}
              </strong>
            </div>

          </aside>

        </div>

      </div>
    </main>
  );
}

export default PaymentMethod;